<template>
  <div class="social-connect container mt-4">
    <div class="card shadow-sm">
      <div class="card-body text-center">
        <h3 class="card-title mb-4">Collega il tuo account Social</h3>
        <p class="card-text mb-4">
          Per iniziare a pubblicare post e creare campagne, devi connettere la tua pagina Facebook aziendale.
        </p>
        
        <!-- Case 1: Account already connected and page selected -->
        <div v-if="isConnected">
          <div class="alert alert-success d-inline-block">
            <i class="fas fa-check-circle me-2"></i> Account Connesso: <strong>{{ pageName }}</strong>
          </div>
          <div class="mt-3">
             <button class="btn btn-outline-danger btn-sm" @click="disconnect">
              Disconnetti
            </button>
          </div>
        </div>
        
        <!-- Case 2: Account connected but needs page selection -->
        <div v-else-if="needsSelection" class="mt-4">
          <div class="alert alert-info">
            <i class="fas fa-list-ul me-2"></i>
            <strong>Seleziona la tua pagina Facebook</strong><br>
            Scegli la pagina su cui desideri pubblicare i post.
          </div>

          <div class="list-group mb-4 text-start">
            <label 
              v-for="page in availablePages" 
              :key="page.id" 
              class="list-group-item list-group-item-action d-flex align-items-center"
              :class="{ 'active': selectedPageId === page.id }"
            >
              <input 
                class="form-check-input me-3" 
                type="radio" 
                :value="page.id" 
                v-model="selectedPageId"
              >
              <div>
                <h6 class="mb-0">{{ page.name }}</h6>
                <small class="text-muted">{{ page.category }}</small>
              </div>
            </label>
          </div>

          <div v-if="availablePages.length === 0" class="alert alert-warning mb-4">
            <strong>Nessuna pagina Facebook trovata per questo account.</strong><br>
            <small>L'account Facebook connesso nel browser non gestisce alcuna Pagina Aziendale, oppure non hai autorizzato l'accesso alla pagina.</small>
          </div>

          <button 
            class="btn btn-success" 
            @click="confirmPageSelection"
            :disabled="!selectedPageId || isLoading"
          >
            <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
            Conferma Selezione
          </button>
          
          <button class="btn btn-outline-danger ms-2" @click="disconnect">
            Disconnetti / Cambia Account
          </button>
          
          <button class="btn btn-outline-secondary ms-2" @click="cancelAuth">
            Annulla
          </button>
        </div>

        <!-- Case 3: Initial state or waiting for auth -->
        <div v-else>
          <div v-if="!isWaitingAuth">
            <button 
              class="btn btn-primary btn-lg" 
              @click="connectFacebook"
              :disabled="isLoading"
            >
              <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
              <i v-else class="fab fa-facebook-f me-2"></i>
              {{ isLoading ? 'Connessione in corso...' : 'Connetti Facebook' }}
            </button>
          </div>
          
          <div v-if="isWaitingAuth" class="mt-4">
            <div class="alert alert-info">
              <i class="fas fa-info-circle me-2"></i>
              <strong>Completa il login nel browser</strong><br>
              Una finestra del browser si è aperta per il login Facebook.<br>
              Dopo aver completato il login, torna qui.
            </div>
            <button class="btn btn-outline-secondary btn-sm" @click="cancelAuth">
              Annulla
            </button>
          </div>
        </div>
        
        <div v-if="errorMessage" class="alert alert-danger mt-3">
          {{ errorMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { store } from '@/store/store.js'

export default {
  name: 'SocialConnect',
  data() {
    return {
      store,
      isConnected: false,
      isLoading: false,
      isWaitingAuth: false,
      pageName: '',
      errorMessage: '',
      pollInterval: null,
      userId: JSON.parse(localStorage.token_id || '{}')._id || 'test_user_id',
      needsSelection: false,
      availablePages: [],
      selectedPageId: null
    }
  },
  mounted() {
    this.checkExistingConnection();
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    async checkExistingConnection() {
      try {
        const apiBase = this.store.path || 'http://localhost:5000/login/';
        const endpoint = apiBase.endsWith('/') 
          ? `${apiBase}social/oauth/status` 
          : `${apiBase}/social/oauth/status`;
        
        const res = await this.axios.get(endpoint, {
          params: { userId: this.userId },
          headers: {
            authorization: `Bearer ${localStorage.token ? JSON.parse(localStorage.token) : ''}`
          }
        });
        
        if (res.data.connected) {
          this.isConnected = true;
          this.pageName = res.data.pageName;
          this.$emit('connection-status', true);
        } else if (res.data.needsSelection) {
          this.needsSelection = true;
          this.availablePages = res.data.pages;
        }
      } catch (error) {
        console.log('No existing connection or error checking status');
      }
    },
    
    async connectFacebook() {
      this.errorMessage = '';
      this.isLoading = true;
      
      try {
        const apiBase = this.store.path || 'http://localhost:5000/login/';
        const endpoint = apiBase.endsWith('/') 
          ? `${apiBase}social/oauth/start` 
          : `${apiBase}/social/oauth/start`;
        
        // Request OAuth URL from backend
        const res = await this.axios.post(endpoint, {
          userId: this.userId
        }, {
          headers: {
            authorization: `Bearer ${localStorage.token ? JSON.parse(localStorage.token) : ''}`
          }
        });
        
        const { authUrl } = res.data;
        
        if (!authUrl) {
          throw new Error('No auth URL received from server');
        }
        
        // Open system browser for login
        this.openInBrowser(authUrl);
        
        // Start polling for completion
        this.isWaitingAuth = true;
        this.startPolling();
        
      } catch (error) {
        console.error('Facebook connect error:', error);
        this.errorMessage = error.response?.data?.message || error.message;
      } finally {
        this.isLoading = false;
      }
    },
    
    openInBrowser(url) {
      // In Electron, use shell.openExternal
      if (window.require) {
        try {
          const { shell } = window.require('electron');
          shell.openExternal(url);
          return;
        } catch (e) {
          console.log('Not in Electron context, using window.open');
        }
      }
      
      // Fallback for web
      window.open(url, '_blank');
    },
    
    startPolling() {
      // Poll every 2 seconds to check if auth completed
      this.pollInterval = setInterval(async () => {
        // Guard: don't process if already connected or no longer waiting
        if (this.isConnected || !this.isWaitingAuth) {
          this.stopPolling();
          return;
        }
        
        try {
          const apiBase = this.store.path || 'http://localhost:5000/login/';
          const endpoint = apiBase.endsWith('/') 
            ? `${apiBase}social/oauth/status` 
            : `${apiBase}/social/oauth/status`;
          
          const res = await this.axios.get(endpoint, {
            params: { userId: this.userId },
            headers: {
              authorization: `Bearer ${localStorage.token ? JSON.parse(localStorage.token) : ''}`
            }
          });
          
          if (res.data.connected && !this.isConnected) {
            // Stop polling FIRST, then update state
            this.stopPolling();
            this.isWaitingAuth = false;
            this.isConnected = true;
            this.pageName = res.data.pageName;
            this.$emit('connection-status', true);
            alert('Connesso con successo alla pagina: ' + this.pageName);
          } else if (res.data.needsSelection && !this.needsSelection) {
            // Account connected but page needs selection
            this.stopPolling();
            this.isWaitingAuth = false;
            this.needsSelection = true;
            this.availablePages = res.data.pages;
          }
        } catch (error) {
          console.error('Polling error:', error);
        }
      }, 2000);
      
      // Stop polling after 5 minutes
      setTimeout(() => {
        if (this.pollInterval) {
          this.stopPolling();
          this.isWaitingAuth = false;
          this.errorMessage = 'Timeout: il login non è stato completato in tempo.';
        }
      }, 5 * 60 * 1000);
    },
    
    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },
    
    cancelAuth() {
      this.stopPolling();
      this.isWaitingAuth = false;
      this.errorMessage = '';
    },
    
    async disconnect() {
      try {
        const apiBase = this.store.path || 'http://localhost:5000/login/';
        const endpoint = apiBase.endsWith('/') 
          ? `${apiBase}social/oauth/disconnect` 
          : `${apiBase}/social/oauth/disconnect`;
        
        await this.axios.post(endpoint, {
          userId: this.userId
        }, {
          headers: {
            authorization: `Bearer ${localStorage.token ? JSON.parse(localStorage.token) : ''}`
          }
        });

        this.isConnected = false;
        this.needsSelection = false;
        this.availablePages = [];
        this.selectedPageId = null;
        this.pageName = '';
        this.$emit('connection-status', false);
      } catch (error) {
        console.error('Disconnect error:', error);
        this.errorMessage = 'Errore durante la disconnessione: ' + (error.response?.data?.message || error.message);
      }
    },

    async confirmPageSelection() {
      if (!this.selectedPageId) {
        this.errorMessage = 'Per favore, seleziona una pagina.';
        return;
      }

      this.isLoading = true;
      this.errorMessage = '';

      try {
        const apiBase = this.store.path || 'http://localhost:5000/login/';
        const endpoint = apiBase.endsWith('/') 
          ? `${apiBase}social/oauth/select-page` 
          : `${apiBase}/social/oauth/select-page`;
        
        const res = await this.axios.post(endpoint, {
          userId: this.userId,
          pageId: this.selectedPageId
        }, {
          headers: {
            authorization: `Bearer ${localStorage.token ? JSON.parse(localStorage.token) : ''}`
          }
        });

        this.isConnected = true;
        this.pageName = res.data.pageName;
        this.needsSelection = false;
        this.$emit('connection-status', true);
        alert('Pagina collegata con successo: ' + this.pageName);
      } catch (error) {
        console.error('Page selection error:', error);
        this.errorMessage = 'Errore durante la selezione della pagina: ' + (error.response?.data?.message || error.message);
      } finally {
        this.isLoading = false;
      }
    }
  }
}
</script>

<style scoped>
.social-connect {
  max-width: 600px;
}
</style>

